import { RSAA, ApiError } from 'redux-api-middleware';
import isEqual from 'lodash.isequal';
import * as Sentry from '@sentry/react';
import { getUserSettings } from './userSettings';
import { loadGeneralSettings } from './generalSettings';
import { Dispatch } from 'redux';
import {
  TGetRootState,
  TAsyncDispatchResult,
  TDispatchResult,
} from 'types/shared';

const GET_REQUEST = 'OPUS/SELECTED_CUSTOMERS/GET_REQUEST';
const PUT_REQUEST = 'OPUS/SELECTED_CUSTOMERS/PUT_REQUEST';
const GET_SUCCESS = 'OPUS/SELECTED_CUSTOMERS/GET_SUCCESS';
const PUT_SUCCESS = 'OPUS/SELECTED_CUSTOMERS/PUT_SUCCESS';
const FAILURE = 'OPUS/SELECTED_CUSTOMERS/FAILURE';

type SelectedCustomersActionTypes =
  | typeof GET_REQUEST
  | typeof PUT_REQUEST
  | typeof GET_SUCCESS
  | typeof PUT_SUCCESS
  | typeof FAILURE;

interface ISelectedCustomersAction {
  type: SelectedCustomersActionTypes;
  error?: boolean;
  payload: any;
}

export interface ISelectedCustomersState {
  pending: boolean;
  fulfilled: boolean;
  rejected: boolean;
  reason: any;
  value: any;
}

const initialState: ISelectedCustomersState = {
  pending: false,
  fulfilled: false,
  rejected: false,
  reason: null,
  value: null,
};

/**
 * * This function removes all selected customer IDs that are not valid
 * by checking them against the actual the customers coming from the API
 *
 * @param {Object} selectedCustomers the possible outdated selected customers coming from the KV store
 * @param {Array} customers the list of all customers coming from the API
 * @returns {Object} the sanitized valid selectedCustomers
 */
function sanitizeSelectedCustomers(
  selectedCustomers: any,
  customers: any[]
): { [key: string]: any } {
  const validCustomers: { [key: string]: any } = {};

  Object.keys(selectedCustomers).forEach((feedType) => {
    validCustomers[feedType] = selectedCustomers[feedType].filter(
      filterCustomers
    );

    function filterCustomers(customer: any): boolean {
      function findSameCustomer(item: any): boolean {
        return item.feedType === feedType && item.name === customer;
      }

      return customers.some(findSameCustomer);
    }
  });

  return validCustomers;
}

export default function reducer(
  state: ISelectedCustomersState = initialState,
  action: ISelectedCustomersAction
): ISelectedCustomersState {
  switch (action.type) {
    case GET_REQUEST:
      return {
        ...state,
        pending: true,
        fulfilled: false,
        rejected: false,
      };
    case PUT_REQUEST:
      return {
        ...state,
        pending: true,
        fulfilled: false,
        rejected: false,
        value: {
          ...state.value,
          ...action.payload,
        },
      };
    case PUT_SUCCESS:
      // We do not update the on put success because we are updating optimistically
      // and the user might already have newer settings once the success comes in.
      // If the PUT call fails the user will see an error message in the DSP selector
      return {
        ...state,
        pending: false,
        fulfilled: true,
        rejected: false,
        reason: null,
      };
    case GET_SUCCESS:
      return {
        ...state,
        pending: false,
        fulfilled: true,
        rejected: false,
        reason: null,
        value: {
          ...state.value,
          ...action.payload,
        },
      };
    case FAILURE:
      return {
        ...state,
        pending: false,
        fulfilled: false,
        rejected: Boolean(action.error),
        reason: action.payload,
      };

    default:
      return state;
  }
}
/**
 * This saves the selected customers to the KV store
 *
 * @param {Array|Object} customers if a key is omitted this should be the complete customers object.
 *                                 If a key is provided only a list of customers for the specific key may be provided
 * @param {String=} key Optional key of the selectedCustomers object. If provided only that specific salesType will be updated.
 */

export function saveSelectedCustomers(
  customers: any | any[],
  key?: string
): TAsyncDispatchResult {
  return async (dispatch: Dispatch, getState: TGetRootState): Promise<any> => {
    const { id } = getState().authentication.profile;
    const { selectedCustomers } = getState();
    const current = selectedCustomers.value;

    /**
     * If we want to save a specific feedType merge it with the current selectedCustomers.
     * Otherwise overwrite the complete set.
     * */
    const payload = key ? { ...current, [key]: customers } : customers;

    return await dispatch({
      [RSAA]: {
        types: [
          {
            type: PUT_REQUEST,
            payload: payload,
          },
          {
            type: PUT_SUCCESS,
            payload: payload,
          },
          FAILURE,
        ],
        endpoint: `/kv/user_${id}_selected_customers`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(payload),
      },
    });
  };
}

/**
 * This action creators load the users' currently selected customers, or
 * it creates a new record in the key/value store.
 */
export function loadSelectedCustomers(): TAsyncDispatchResult {
  return async function (
    dispatch: Dispatch,
    getState: TGetRootState
  ): Promise<any> {
    // get the user id
    const { id } = getState().authentication.profile;
    const customers = getState().customers.data;

    let selectedCustomersAreOutdated = false;
    // fetch the users' selected customers
    const actionResponse: any = await dispatch({
      [RSAA]: {
        types: [
          GET_REQUEST,
          {
            type: GET_SUCCESS,
            payload: async function payload(
              action: ISelectedCustomersAction,
              state: ISelectedCustomersState,
              res: any
            ): Promise<any> {
              const selectedCustomers = await res.json();

              const sanitizedSelectedCustomers = sanitizeSelectedCustomers(
                selectedCustomers,
                customers
              );

              // check if there are differences between the sanitized and the original values
              if (!isEqual(sanitizedSelectedCustomers, selectedCustomers)) {
                selectedCustomersAreOutdated = true;
              }

              return sanitizedSelectedCustomers;
            },
          },
          FAILURE,
        ],
        endpoint: `/kv/user_${id}_selected_customers`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
      },
    });

    if (actionResponse.error) {
      /**
       * If the status code is a 404, the selected customers for this user
       * need to be initialized.
       */
      if ((actionResponse.payload as ApiError<any>).status === 404) {
        return await dispatch(initSelectedCustomers(actionResponse) as any);
      }

      /**
       * If the error did not have a 404 status code, something else is going on,
       * report the error to Sentry and throw.
       */
      Sentry.withScope((scope: any) => {
        scope.setExtras({ response: actionResponse, state: getState() });
        Sentry.captureException(actionResponse.payload);
      });

      throw actionResponse.payload;
    }

    if (shouldInitSelectedCustomers(actionResponse)) {
      return await dispatch(initSelectedCustomers(actionResponse) as any);
    } else if (selectedCustomersAreOutdated) {
      // if the selectedCustomers in the KV store are outdated, update them with the sanitized values
      dispatch(saveSelectedCustomers(actionResponse.payload) as any);
    }

    return actionResponse;
  };
}

/**
 * This function returns true when the selected customers need to be (re-)initialized.
 *
 * @param {Object} res Selected customers Response object
 */
function shouldInitSelectedCustomers(res: any): boolean {
  // If streaming or downloads do not exist, return true.
  if (!('streaming' in res.payload)) return true;
  if (!('downloads' in res.payload)) return true;

  // If streaming or downloads are falsy, return true.
  if (!res.payload.streaming) return true;
  if (!res.payload.downloads) return true;

  // If streaming or download are zero-length, return true.
  if (res.payload.streaming.length === 0) return true;
  if (res.payload.downloads.length === 0) return true;

  // Everything is ok, return false.
  return false;
}

export const shouldLoadSelectedCustomers = (): TDispatchResult<boolean> => {
  return (_: Dispatch, getState: TGetRootState): boolean => {
    const state = getState();
    const { selectedCustomers } = state;
    const { pending, value } = selectedCustomers;
    return !value && !pending;
  };
};

/**
 * USER SELECTED CUSTOMERS INITIALIZATION FLOW
 *
 * 1. Fetch the legacy userSettings,
 * 2. Fetch the generalSettings
 * 3. Check if the selectedCustomers already exist, and initialize the object with them, or
 * 4. Check if the legacy selected customers have selected customers in them and initialize the object with them, or
 * 5. Use the default selected customers.
 *
 * If a user has a previously set selectedCustomers object in the
 * userSettings, we should use that.
 *
 * If not, we should use the default selectedCustomers from the general settings.
 *
 */
function initSelectedCustomers(res: any): TAsyncDispatchResult {
  return async (dispatch: Dispatch, getState: TGetRootState): Promise<any> => {
    const { id } = getState().authentication.profile;
    let streaming, downloads;

    try {
      /**
       * Fetch the legacy user settings and the default settings.
       */
      const userSettings = await dispatch(getUserSettings());
      const generalSettings = await dispatch(loadGeneralSettings() as any);

      /**
       * The user settings might not have been initialized for this user,
       * in that case, we can safely use the general settings.
       *
       * In short, it is OK to get a 404 status code here.
       */
      if (userSettings.error) {
        if (userSettings.payload.status !== 404) {
          throw userSettings.payload;
        }
      }

      /**
       * Try to set legacyStreaming and legacyDownloads.
       * They can be undefined and that's ok.
       */
      let legacyStreaming, legacyDownloads;
      if ('selectedCustomers' in userSettings.payload) {
        ({
          selectedCustomers: {
            streamingCustomers: legacyStreaming,
            downloadCustomers: legacyDownloads,
          },
        } = userSettings.payload);
      }

      if (generalSettings.error) throw generalSettings.payload;

      const {
        defaultCustomers: {
          streamingCustomers: defaultStreaming,
          downloadCustomers: defaultDownloads,
        },
      } = generalSettings.payload;

      /**
       * Try to set the new selected customers types based on
       * currently existing selectedCustomers (in the case of a re-init).
       */
      ({ streaming, downloads } = res.payload);

      /**
       * Set streaming if necessary
       */
      if (!streaming || streaming.length === 0) {
        if (legacyStreaming && legacyStreaming.length > 0) {
          streaming = legacyStreaming;
        } else {
          streaming = defaultStreaming;
        }
      }

      /**
       * Set downloads if necessary
       */
      if (!downloads || downloads.length === 0) {
        if (legacyDownloads && legacyDownloads.length > 0) {
          downloads = legacyDownloads;
        } else {
          downloads = defaultDownloads;
        }
      }
    } catch (error) {
      /**
       * Catch any errors that occur in the above logic and report them to Sentry and throw.
       */
      Sentry.withScope((scope: any) => {
        scope.setExtras({ state: getState() });
        Sentry.captureException(error);
      });

      throw error;
    }

    // dispatch the action
    return await dispatch({
      [RSAA]: {
        types: [
          {
            type: PUT_REQUEST,
            payload: { streaming, downloads },
          },
          {
            type: PUT_SUCCESS,
            payload: { streaming, downloads },
          },
          FAILURE,
        ],
        endpoint: `/kv/user_${id}_selected_customers`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ streaming, downloads }),
      },
    });
  };
}
